import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getBirdsEyeView } from '../../_services/schedules.service';
import { ActivitySlot, BirdsEyeViewData } from '../../_models/schedule.model';
import './centre-display-board.css';

type ActivityStatus = 'ontime' | 'ongoing' | 'delayed' | 'completed' | 'cancelled';

interface EnrichedActivitySlot extends ActivitySlot {
  calculatedStatus: ActivityStatus;
  timeUntilStart?: string;
  isInProgress?: boolean;
}

const CentreDisplayBoard: React.FC = () => {
  const { centreId } = useParams<{ centreId: string }>();
  const location = useLocation();

  // Parse query parameters manually
  const searchParams = new URLSearchParams(location.search);
  const centreName = searchParams.get('centreName') || 'Centre';

  const [activities, setActivities] = useState<EnrichedActivitySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [analytics, setAnalytics] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    scheduled: 0,
  });

  // Pagination state for TV display auto-scrolling
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(8); // Fixed at 8 items per page for TV display
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Calculate activity status based on current time
  const calculateActivityStatus = useCallback((slot: ActivitySlot, now: Date): ActivityStatus => {
    // Handle cancelled slots
    if (slot.slotStatus === 'Cancelled' || slot.status === 'Cancelled') {
      return 'cancelled';
    }

    // Parse date and time
    if (!slot.date || !slot.startTime || !slot.endTime) {
      return 'ontime';
    }

    try {
      const startTime = new Date(`${slot.date} ${slot.startTime}`);
      const endTime = new Date(`${slot.date} ${slot.endTime}`);

      // Check if activity is in the past
      if (now > endTime) {
        return slot.slotStatus === 'Conducted' ? 'completed' : 'completed';
      }

      // Check if activity is currently ongoing
      if (now >= startTime && now <= endTime) {
        return 'ongoing';
      }

      // Activity is scheduled for future
      if (now < startTime) {
        return 'ontime';
      }

      return 'ontime';
    } catch (error) {
      console.error('Error calculating status:', error);
      return 'ontime';
    }
  }, []);

  // Calculate time until start
  const calculateTimeUntilStart = useCallback((slot: ActivitySlot, now: Date): string | undefined => {
    if (!slot.date || !slot.startTime) return undefined;

    try {
      const startTime = new Date(`${slot.date} ${slot.startTime}`);
      const diff = startTime.getTime() - now.getTime();

      if (diff < 0) return undefined; // Already started

      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);

      if (hours > 0) {
        return `Starts in ${hours}h ${minutes % 60}m`;
      } else if (minutes > 0) {
        return `Starts in ${minutes} min`;
      } else {
        return 'Starting now';
      }
    } catch (error) {
      return undefined;
    }
  }, []);

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    if (!centreId) {
      setError('No centre ID provided');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const today = new Date().toISOString().split('T')[0];

      const response = await getBirdsEyeView({
        centreIds: centreId,
        startDate: today,
        endDate: today,
      });

      if (response.success && response.data) {
        const now = new Date();

        // Enrich activities with calculated status
        const enriched: EnrichedActivitySlot[] = response.data.slots
          .map(slot => ({
            ...slot,
            calculatedStatus: calculateActivityStatus(slot, now),
            timeUntilStart: calculateTimeUntilStart(slot, now),
            isInProgress: calculateActivityStatus(slot, now) === 'ongoing',
          }))
          .sort((a, b) => {
            // Sort by start time
            const timeA = a.startTime || '';
            const timeB = b.startTime || '';
            return timeA.localeCompare(timeB);
          });

        setActivities(enriched);

        // Calculate analytics
        const stats = {
          total: enriched.length,
          inProgress: enriched.filter(a => a.calculatedStatus === 'ongoing').length,
          completed: enriched.filter(a => a.calculatedStatus === 'completed').length,
          cancelled: enriched.filter(a => a.calculatedStatus === 'cancelled').length,
          scheduled: enriched.filter(a => a.calculatedStatus === 'ontime').length,
        };
        setAnalytics(stats);
        setLastUpdated(new Date());
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities. Please check your connection.');
      setLoading(false);
    }
  }, [centreId, calculateActivityStatus, calculateTimeUntilStart]);

  // Initial fetch and auto-refresh every 30 seconds
  useEffect(() => {
    fetchActivities();
    const interval = setInterval(fetchActivities, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [fetchActivities]);

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Recalculate status every minute based on current time
  useEffect(() => {
    if (activities.length === 0) return;

    const interval = setInterval(() => {
      const now = new Date();
      setActivities(prev => prev.map(slot => ({
        ...slot,
        calculatedStatus: calculateActivityStatus(slot, now),
        timeUntilStart: calculateTimeUntilStart(slot, now),
        isInProgress: calculateActivityStatus(slot, now) === 'ongoing',
      })));
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [activities.length, calculateActivityStatus, calculateTimeUntilStart]);

  // Auto-advance pages for TV display (every 10 seconds)
  useEffect(() => {
    if (loading || activities.length === 0) return;

    const totalPages = Math.ceil(activities.length / itemsPerPage);

    // Only paginate if we have more than one page
    if (totalPages <= 1) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);

      // Transition effect
      setTimeout(() => {
        setCurrentPage((prevPage) => {
          const nextPage = prevPage + 1;
          return nextPage >= totalPages ? 0 : nextPage;
        });
        setIsTransitioning(false);
      }, 300); // Match CSS transition duration
    }, 10000); // 10 seconds per page

    return () => clearInterval(interval);
  }, [activities.length, itemsPerPage, loading]);

  // Format time for display
  const formatTime = (time?: string) => {
    if (!time) return 'N/A';
    try {
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return time;
    }
  };

  // Format last updated time
  const getLastUpdatedText = () => {
    const seconds = Math.floor((currentTime.getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  // Get status label
  const getStatusLabel = (status: ActivityStatus): string => {
    switch (status) {
      case 'ontime': return 'On Time';
      case 'ongoing': return 'In Progress';
      case 'delayed': return 'Delayed';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  // Get time meta info
  const getTimeMeta = (slot: EnrichedActivitySlot): string => {
    if (slot.calculatedStatus === 'cancelled') {
      return slot.remarks || 'Cancelled';
    }
    if (slot.calculatedStatus === 'completed') {
      return 'Completed';
    }
    if (slot.calculatedStatus === 'ongoing') {
      return slot.studentCount ? `In progress · ${slot.studentCount} students` : 'In progress';
    }
    return slot.timeUntilStart || '';
  };

  return (
    <div className="display-board-container">
      {/* Header */}
      <header className="display-header">
        <div className="display-header-left">
          <h1 className="display-title">Campus Live Schedule</h1>
          <p className="display-subtitle">Real-time status of all classes at {centreName}</p>
        </div>
        <div className="display-header-right">
          <div className="display-pill">
            <span className="display-pill-dot"></span>
            <span>Live feed connected</span>
          </div>
          <div className="display-time-info">
            <div className="display-clock">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
            <div className="display-date">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>
      </header>

      {/* Filters/Info Row */}
      <section className="display-filters">
        <div className="display-filters-left">
          <div className="display-filter-item">
            <span className="filter-label">Centre</span>
            <span className="filter-value">{centreName}</span>
          </div>
          <div className="display-filter-item">
            <span className="filter-label">Window</span>
            <span>Today · All Classes</span>
          </div>
        </div>
        <div className="display-filters-right">
          <div className="display-legend">
            <span className="legend-item">
              <span className="legend-dot ontime"></span> On time / Yet to start
            </span>
            <span className="legend-item">
              <span className="legend-dot ongoing"></span> In progress
            </span>
            <span className="legend-item">
              <span className="legend-dot delayed"></span> Delayed / Room change
            </span>
            <span className="legend-item">
              <span className="legend-dot completed"></span> Completed
            </span>
            <span className="legend-item">
              <span className="legend-dot cancelled"></span> Cancelled
            </span>
          </div>
        </div>
      </section>

      {/* Main Board */}
      <main className="display-board-wrapper">
        <div className="display-board-card">
          <div className="display-board-header-row">
            <span>
              <strong>Activity Slots</strong> · {analytics.total} classes scheduled · {analytics.inProgress} in progress · {analytics.completed} completed
            </span>
            <span>Last updated: {getLastUpdatedText()}</span>
          </div>

          <div className="display-scroll-area">
            <div className="display-table-container">
              {loading ? (
                <div className="display-loading">Loading activities...</div>
              ) : error ? (
                <div className="display-error">
                  <div className="display-error-icon">⚠️</div>
                  <div className="display-error-text">{error}</div>
                </div>
              ) : activities.length === 0 ? (
                <div className="display-empty">
                  <div className="display-empty-icon">📅</div>
                  <div className="display-empty-text">No activities scheduled for today</div>
                </div>
              ) : (
                <table className="display-table">
                  <thead>
                    <tr>
                      <th className="col-time">Time</th>
                      <th className="col-room">Room</th>
                      <th className="col-building">Shift</th>
                      <th className="col-topic">Topic / Module</th>
                      <th className="col-instructor">Instructor</th>
                      <th className="col-cohort">Cohort</th>
                      <th className="col-status">Status</th>
                    </tr>
                  </thead>
                  <tbody className={isTransitioning ? 'fade-out' : 'fade-in'}>
                    {activities
                      .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
                      .map((slot) => (
                      <tr key={slot.id}>
                        <td className="col-time">
                          <div className="time-range">
                            <span>{formatTime(slot.startTime)} – {formatTime(slot.endTime)}</span>
                            {getTimeMeta(slot) && (
                              <span className="time-meta">{getTimeMeta(slot)}</span>
                            )}
                          </div>
                        </td>
                        <td className="col-room">
                          <span className={`room-tag ${slot.calculatedStatus === 'delayed' ? 'warning' : ''}`}>
                            <span className="room-tag-badge"></span>
                            {slot.roomName || 'TBA'}
                          </span>
                        </td>
                        <td className="col-building">{slot.shiftName || 'N/A'}</td>
                        <td className="col-topic">
                          <div className="topic-display">
                            {slot.topicName && <span className="topic-name">{slot.topicName}</span>}
                            {!slot.topicName && <span className="topic-name">{slot.name || 'Unnamed Activity'}</span>}
                          </div>
                        </td>
                        <td className="col-instructor">
                          <div className="instructor-display">
                            <span className="instructor-name">{slot.instructorName || 'TBA'}</span>
                            {slot.instructorId && (
                              <span className="instructor-id">ID-{slot.instructorId}</span>
                            )}
                          </div>
                        </td>
                        <td className="col-cohort">
                          <div className="cohort-display">
                            {slot.batches && slot.batches.length > 0
                              ? slot.batches.map(b => b.batchName).join(', ')
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="col-status">
                          <span className={`status-pill status-${slot.calculatedStatus}`}>
                            {getStatusLabel(slot.calculatedStatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Page Indicator - only show if multiple pages */}
      {!loading && activities.length > itemsPerPage && (
        <div className="display-page-indicator">
          <span className="page-dots">
            {Array.from({ length: Math.ceil(activities.length / itemsPerPage) }).map((_, index) => (
              <span
                key={index}
                className={`page-dot ${index === currentPage ? 'active' : ''}`}
              />
            ))}
          </span>
          <span className="page-text">
            Page {currentPage + 1} of {Math.ceil(activities.length / itemsPerPage)}
          </span>
        </div>
      )}

      {/* Footer */}
      <footer className="display-footer">
        <div className="display-ticker">
          <span>
            Welcome to the {centreName} Live Schedule Board.
            This display updates automatically every 30 seconds.
            Please check with your instructor for any last-minute changes.
          </span>
        </div>
        <div className="display-branding">
          Powered by <strong>EduSquares · TimeCraft</strong>
        </div>
      </footer>
    </div>
  );
};

export default CentreDisplayBoard;
